import React, { ComponentType } from 'react'

import { SxProps, Theme } from '@mui/material/styles'
import Autocomplete, { AutocompleteProps, AutocompleteRenderInputParams } from '@mui/material/Autocomplete'
import { InputBaseProps } from '@mui/material/InputBase'
import { PopperProps } from '@mui/material/Popper'

import TextInput from '@/components/ui/inputs/TextInput'

export type AutocompleteOption = {
  label: string
  value: string
}

export type AutocompleteValue = AutocompleteOption | AutocompleteOption[] | null

export type AutocompleteInputProps = {
  /**
   * If `true`, the component is displayed in an error state.
   * @default false
   */
  error?: boolean
  fullWidth?: boolean
  freeSolo?: boolean
  /**
   * The helper text content.
   */
  helperText?: React.ReactNode
  inputValue?: string
  label?: React.ReactNode
  multiple?: boolean
  name?: string
  onBlur?: InputBaseProps['onBlur']
  onChange?: (newValue: AutocompleteValue) => void
  onInputChange?: (inputValue: string, reason: string) => void
  /**
   * Callback fired when the popup requests to be opened. Use in controlled mode (see open).
   * @param event The event source of the callback.
   * @returns
   */
  onOpen?: (event: React.SyntheticEvent) => void
  options: AutocompleteOption[]
  /**
   * If true, the component is shown.
   */
  open?: boolean
  placeholder?: string
  PopperComponent?: ComponentType<PopperProps>
  readOnly?: boolean
  renderTags?: AutocompleteProps<AutocompleteOption, boolean, boolean, boolean>['renderTags']
  value?: AutocompleteValue
  sx?: SxProps<Theme>
  disabled?: boolean
  loading?: boolean
  renderInput?: (params: AutocompleteRenderInputParams) => React.ReactNode
}

export function AutocompleteInput(props: AutocompleteInputProps) {
  const {
    error = false,
    fullWidth = false,
    freeSolo = true,
    helperText,
    inputValue,
    label,
    multiple = false,
    name,
    onBlur,
    onChange,
    onInputChange,
    onOpen,
    options,
    open,
    placeholder,
    PopperComponent,
    readOnly = false,
    renderTags,
    value,
    sx = [],
    disabled = false,
    loading = false,
    renderInput,
  } = props

  const handleChange = (_event: React.SyntheticEvent, newValue: string | AutocompleteOption | Array<string | AutocompleteOption> | null) => {
    if (typeof newValue === 'string' || (Array.isArray(newValue) && newValue.length > 0 && newValue.every((v) => typeof v === 'string'))) return
    onChange?.(newValue as AutocompleteValue)
  }

  const handleInputChange = (_event: React.SyntheticEvent, inputValue: string, reason: string) => {
    onInputChange?.(inputValue, reason)
  }

  return (
    <Autocomplete
      disablePortal
      filterSelectedOptions
      fullWidth={fullWidth}
      freeSolo={freeSolo}
      getOptionLabel={(option: string | AutocompleteOption) => (typeof option === 'string' ? option : option.label ?? '')}
      isOptionEqualToValue={(option, val) => option.value === val.value}
      inputValue={inputValue}
      multiple={multiple}
      onChange={handleChange}
      onInputChange={handleInputChange}
      onOpen={onOpen}
      options={options}
      open={open}
      loading={loading}
      PopperComponent={PopperComponent}
      readOnly={readOnly}
      disabled={disabled}
      renderInput={
        renderInput
          ? renderInput
          : (params) => (
              <TextInput
                {...params}
                error={error}
                helperText={helperText}
                inputProps={{
                  ...params.inputProps,
                  onBlur: (event: React.FocusEvent<HTMLInputElement>) => {
                    params.inputProps.onBlur?.(event)
                    onBlur?.(event)
                  },
                }}
                label={label}
                name={name}
                placeholder={placeholder}
              />
            )
      }
      renderTags={multiple ? renderTags : undefined}
      sx={[
        {
          '.MuiOutlinedInput-root': {
            padding: 0,
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      value={value}
    />
  )
}

export default AutocompleteInput
